import React from 'react';
import { TypographyProps, TypographyVariant, TypographyElement } from '../../../types/DesignSystem/Typography';
import { withClassName } from '../cn';

/*
 * § 263 — `text-wrap: balance` and `truncate` write the SAME longhand, and balance
 * wins. `tw-truncate` is `overflow:hidden; text-overflow:ellipsis; white-space:nowrap`,
 * and in modern CSS `white-space: nowrap` sets `text-wrap-mode: nowrap`, while
 * `text-wrap: balance` sets `text-wrap-style: balance` AND `text-wrap-mode: wrap`.
 * Tailwind emits `.tw-text-balance` after `.tw-truncate`, so the heading wraps — and
 * balances, which is why a too-narrow box produced four lines of equal length rather
 * than one line with an ellipsis. Measured live 2026-09-12 on the Compatibility rule
 * header: an element carrying `tw-truncate` computed `white-space: normal`.
 *
 * So a heading could never be truncated, in ANY consumer, and the failure was silent.
 * `BALANCE_CONFLICTS` names the caller-supplied classes that mean "I am clamping this
 * line myself"; when one is present the balance class is dropped and the caller's
 * intent wins. Everything else keeps balance exactly as § 243 shipped it.
 */
const BALANCE = 'tw-text-balance';
/*
 * Whole class names, matched token-wise — `tw-truncated-thing` must not strip balance
 * off a heading that never asked for it.
 *
 * Two shapes here rather than one regex, and the reason is a house trap: the app-wide
 * class scanners (`classIntegrity.appWide`, `tokenGuard`) read `tw-…` sequences out of
 * this source and check each one emits CSS. A regex alternation like `tw-(?:truncate|…)`
 * registers as a dead class called `tw-(`. So the two fixed classes are spelled in full
 * (they are real utilities and the scanners are happy), while the `line-clamp-N` FAMILY
 * is matched on its suffix with the prefix taken off `BALANCE` instead of written out.
 */
const CLAMP_CLASSES = ['tw-truncate', 'tw-whitespace-nowrap'];
const TW = BALANCE.slice(0, 3);
const LINE_CLAMP = /^line-clamp-\d+$/;

const isClamped = (className: string): boolean =>
  className.split(/\s+/).some(
    (c) => CLAMP_CLASSES.includes(c) || (c.startsWith(TW) && LINE_CLAMP.test(c.slice(TW.length))),
  );

const variantToClass: Record<TypographyVariant, string> = {
  /* Headings carry `text-wrap: balance` — a two-line title breaks evenly
     instead of stranding one word; single-line titles are untouched. Body and
     label tiers stay natural: balance would reflow dense running text.
     Dropped when the caller is clamping the line — see § 263 above. */
  'display-2': 'tw-text-display-2 tw-text-balance',
  display: 'tw-text-display tw-text-balance',
  'heading-1': 'tw-text-heading-1 tw-text-balance',
  'heading-2': 'tw-text-heading-2 tw-text-balance',
  'heading-3': 'tw-text-heading-3 tw-text-balance',
  'title-header': 'tw-text-title-header',
  title: 'tw-text-title',
  'body-large': 'tw-text-body-large',
  'body-primary': 'tw-text-body-primary',
  'sub-title-header': 'tw-text-sub-title-header',
  'sub-title-label': 'tw-text-sub-title-label',
  'sub-body-supporting': 'tw-text-sub-body-supporting',
  'caption-metadata': 'tw-text-caption-metadata',
  'code-formula': 'tw-text-code-formula',
};

const defaultElements: Record<TypographyVariant, TypographyElement> = {
  'display-2': 'h1',
  display: 'h1',
  'heading-1': 'h2',
  'heading-2': 'h3',
  'heading-3': 'h4',
  'title-header': 'h4',
  title: 'h5',
  'body-large': 'p',
  'body-primary': 'p',
  'sub-title-header': 'h6',
  'sub-title-label': 'span',
  'sub-body-supporting': 'span',
  'caption-metadata': 'span',
  'code-formula': 'code',
};

const Typography = React.forwardRef<HTMLElement, TypographyProps>(function Typography({
  variant,
  as,
  children,
  className = '',
  ...rest
}, ref) {
  const Component = as || defaultElements[variant];
  /* § 263 — a caller that truncates or clamps has asked for a fixed number of lines;
     balance would silently overrule it. Removing the token is enough: the conflict is
     between two utilities on one element, not between two elements. */
  const textClass = isClamped(className)
    ? variantToClass[variant].replace(BALANCE, '').trim()
    : variantToClass[variant];

  return React.createElement(
    Component,
    {
      // `rest` first: `className` is composed here and must not be overwritten by
      // a stray one, while everything else — `id`, `title`, `aria-*`, `onClick`,
      // `data-testid` — is the caller's to set.
      ...rest,
      ref,
      className: withClassName(`${textClass}`, className),
    },
    children
  );
});

export default Typography;
